/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { SelectionDetail } from '../UNSAFE_Collection';
import { Flex } from '../UNSAFE_Flex';
import { usePress } from '../hooks/UNSAFE_usePress';
import { styles } from '../UNSAFE_Selector/themes/SelectorStyles.css';
import { StyledCheckbox } from '../PRIVATE_StyledCheckbox';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

/**
 * Props for the SelectorAll Component
 */
export type Props<K> = {
  /**
   * The selected state of the select all selector
   */
  selected: 'none' | 'all' | 'partial' | 'partial-all';
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
  /**
   * aria-label for this selectorAll
   */
  'aria-label'?: string;
  /**
   * One or more ids (separated by spaces) of elements that label the selected item. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;
};

/**
 * The SelectorAll component renders checkboxes for select all case.
 */
export function SelectorAll<K>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  selected,
  onChange,
  testId
}: Props<K> & TestIdProps) {
  const pressHandler = (event: Event) => {
    if (selected == 'none' || selected == 'partial') {
      onChange?.({ value: { all: true, deletedKeys: new Set() }, target: event.target });
    } else {
      onChange?.({ value: { all: false, keys: new Set() }, target: event.target });
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const { isActive, activeProps } = useActive();
  const { isFocus, focusProps } = useFocus();
  const { pressProps } = usePress(pressHandler);
  const isChecked =
    selected === 'partial' || selected === 'partial-all'
      ? 'partial'
      : selected === 'all'
      ? 'checked'
      : 'unchecked';
  const testIdProps = useTestId(testId);

  const onClick = () => {
    // ensure the checkbox is focused after click
    inputRef.current?.focus();
  };

  return (
    <div class={styles.container} {...mergeProps({ onClick }, pressProps)} {...testIdProps}>
      <div class={styles.base} {...activeProps}>
        <Flex align="center" justify="center" width="11x" height="11x">
          <StyledCheckbox
            ref={inputRef}
            isChecked={isChecked}
            isActive={isActive}
            isFocusRingShown={isFocus}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            onKeyDown={handleKeyboardEvent}
            onKeyUp={handleKeyboardEvent}
            {...focusProps}
          />
        </Flex>
      </div>
    </div>
  );
}

const handleKeyboardEvent = (event: KeyboardEvent) => {
  if (event.key == 'Enter') {
    // prevent active styles applied to checkbox
    event.stopPropagation();
  }
};
